import { Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PPULocationModel } from '@app/shared/models/location.model';
import { PageTitleService } from '@app/shared/services/page-title.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-top-nav',
    templateUrl: './top-nav.component.html',
    standalone: false
})
export class TopNavComponent implements OnInit, OnChanges, OnDestroy {
  pageTitle: string = '';
  @Input() locationName: string = '';
  @Input() isAdminRole: boolean = false;
  @Input() currentLocation: number = 0;
  @Input() locations: Array<PPULocationModel> | null = null;
  @Input() isMobile: boolean = false;
  locationFilterCtrl: FormControl<string | null> = new FormControl<string>('');
  filteredLocations: Array<PPULocationModel> = [];
  
  @Output() viewAccountEvent = new EventEmitter<any>();
  @Output() changeLocation = new EventEmitter<number>();
  @Output() toggleMobileNav = new EventEmitter<void>();

  private destroyed$ = new Subject<void>();

  constructor(
    public translate: TranslateService,
    private pageTitleService: PageTitleService,
    private router: Router,
  ) {}
  ngOnInit() {
    this.pageTitleService.pageTitle.subscribe(title => {
      this.pageTitle = title;
    });

    // initialize filtered list
    this.filteredLocations = this.locations ?? [];

    // apply filtering on search changes
    this.locationFilterCtrl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(searchTerm => {
        const term = (searchTerm ?? '').toString();
        this.applyLocationFilter(term);
      });
    }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['locations']) {
      this.filteredLocations = this.locations ?? [];
      const currentTerm = this.locationFilterCtrl.value ?? '';
      this.applyLocationFilter(currentTerm);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public toggleLanguage() {
    const language = this.translate.currentLang == 'en' ? 'fr' : 'en';
    this.translate.use(language);
    localStorage.setItem('ppuLanguagePreference', language);
  }

  public changeLocationSelector(value: number) {
    this.changeLocation.emit(value);
  }

  public showAccountDetail() {
    this.viewAccountEvent.emit();
  }

  public onToggleMobileNav() {
    this.toggleMobileNav.emit();
  }

  private applyLocationFilter(search: string) {
    const query = search.trim().toLowerCase();
    if (!this.locations || !query) {
      this.filteredLocations = this.locations ?? [];
      return;
    }

    this.filteredLocations = this.locations.filter(loc => {
      const name = (loc.locationName ?? '').toLowerCase();
      const address = (loc.addressLine2 ?? '').toLowerCase();
      const idStr = String(loc.id ?? '').toLowerCase();
      return name.includes(query) || address.includes(query) || idStr.includes(query);
    });
  }

  public get selectedLocation(): PPULocationModel | null {
    if (!this.locations) return null;
    return this.locations.find(l => l.id === this.currentLocation) ?? null;
  }

  public isSelectedInFiltered(): boolean {
    if (!this.filteredLocations?.length) return false;
    return this.filteredLocations.some(l => l.id === this.currentLocation);
  }

  public get isAdminModule(): boolean {
    return this.router.url.split('?')[0].includes('/admintool');
  }
}
